import React, { useState } from "react";
import { INCIDENT_IMAGES, CATEGORY_META, ACCURACY_META } from "../constants/content";
import { Mascot } from "./Mascot";
import { CringeMeter } from "./CringeMeter";
import { ReactionButtons } from "./ReactionButtons";
import { ShareButtons } from "./ShareButtons";
import { ExternalLink, PlayCircle, ShieldCheck, Zap } from "lucide-react";

export const IncidentCard = ({ incident, variant = "default", index = 0, rank, anchor = false }) => {
  const [reactions, setReactions] = useState(incident.reactions || { fire: 0, skull: 0, laugh: 0 });
  const img = INCIDENT_IMAGES[incident.image_key];
  const cat = CATEGORY_META[incident.category] || CATEGORY_META.all;
  const acc = ACCURACY_META[incident.accuracy] || ACCURACY_META.pattern;
  const AccIcon = incident.accuracy === "verified" ? ShieldCheck : Zap;
  const extreme = incident.cringe >= 9;
  const clips = incident.clips || [];

  const shadow =
    index % 3 === 0 ? "brutal-shadow-y" : index % 3 === 1 ? "brutal-shadow-r" : "brutal-shadow-m";

  return (
    <article
      id={anchor ? `incident-${incident.id}` : undefined}
      data-testid={`incident-card-${incident.id}`}
      className={`relative bg-[#18181b] brutal-border ${shadow} overflow-hidden`}
      style={variant === "podium" ? { minHeight: 380 } : undefined}
    >
      {/* Rank corner */}
      {typeof rank === "number" && (
        <div
          className="absolute -top-1 -left-1 z-10 font-mono text-xs px-2 py-1 brutal-border"
          style={{ background: rank === 1 ? "#FF00FF" : rank === 2 ? "#FFEA00" : "#FF003C", color: rank === 1 || rank === 3 ? "#fff" : "#000" }}
        >
          RANK #{rank}
        </div>
      )}

      <div className="relative">
        <img
          src={img}
          alt={incident.title}
          className="w-full h-44 object-cover grayscale contrast-125"
          loading="lazy"
        />
        {/* Red tape overlay */}
        <div
          className="absolute top-3 right-0 translate-x-2 -rotate-6 px-3 py-1 font-bold text-[11px] uppercase"
          style={{ background: "#FF003C", color: "#fff", letterSpacing: "0.08em" }}
        >
          EXHIBIT {incident.id.slice(0, 4).toUpperCase()}
        </div>
        {/* Accuracy stamp */}
        <div
          className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 brutal-border font-mono text-[10px] font-bold uppercase"
          style={{ background: acc.bg, color: acc.fg, borderWidth: 3 }}
          title={acc.note}
          data-testid={`accuracy-${incident.id}`}
        >
          <AccIcon size={12} strokeWidth={3} />
          {acc.label}
        </div>
        {/* Mascot stamp */}
        <div className="absolute -bottom-6 left-4">
          <Mascot size={64} shake={extreme} />
        </div>
      </div>

      <div className="pt-10 p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`font-mono text-[10px] px-2 py-1 brutal-border ${cat.color}`}
            style={{ borderWidth: 2 }}
          >
            {cat.label}
          </span>
          <span className="font-mono text-[11px] text-white/60 uppercase">
            {incident.date} · {incident.year}
          </span>
        </div>

        <h3 className="heading text-2xl md:text-3xl text-white leading-[0.95]">
          {incident.title}
        </h3>

        <p className="text-sm text-white/80 leading-relaxed">
          {incident.description}
        </p>

        <CringeMeter value={incident.cringe} testId={`cringe-${incident.id}`} />

        {clips.length > 0 && (
          <div
            className="brutal-border bg-black p-3 space-y-2"
            style={{ borderWidth: 3 }}
            data-testid={`clips-${incident.id}`}
          >
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-[#FFEA00]">
              <PlayCircle size={14} strokeWidth={3} />
              Public Evidence
            </div>
            <ul className="space-y-1">
              {clips.map((c, i) => (
                <li key={`${c.url}-${i}`}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`clip-link-${incident.id}-${i}`}
                    className="group inline-flex items-center gap-2 font-mono text-[11px] text-white hover:text-[#FFEA00] transition-colors duration-100"
                  >
                    <span className="text-[#FF003C]">▸</span>
                    <span className="underline underline-offset-2 decoration-dotted">
                      {c.label}
                    </span>
                    <span className="text-white/40">[{c.source}]</span>
                    <ExternalLink size={11} strokeWidth={3} className="opacity-60 group-hover:opacity-100" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ReactionButtons
          incidentId={incident.id}
          reactions={reactions}
          onUpdate={setReactions}
        />

        <ShareButtons incident={incident} />
      </div>
    </article>
  );
};
